<template>
  <div ref="container" class="fallback-hint-container position-relative" :data-attribute="field" v-on-click-outside.focus="handleClickOutside">
    <div class="d-flex">
      <div
        class="input-group flex-nowrap"
        :class="{
          'border-warning': localValidationData && !localValidationData.isValid && localValidationData.type == 'warning',
          'border-danger': localValidationData && !localValidationData.isValid && localValidationData.type == 'error'
        }"
        ref="inputWrapper"
        data-cy="single-value-fallback-input-group">
        <component
          :is="dataTypeEditor"
          v-model="localValue"
          :valueOrFallback="valueOrFallback.value"
          :validationData="localValidationData"
          :language="language"
          :dataType="dataType"
          :authorization-context="authorizationContext"></component>
        <input
          v-if="!dataTypeEditor"
          :value="localValue"
          ref="input"
          :readonly="isReadonly"
          class="form-control value-editor"
          v-safe-class="inputClass"
          :placeholder="focused ? '' : valueOrFallback.value"
          @input="localValue = $event.target.value"
          @focus="focused = true"
          @blur="onBlurHandler"
          @change="$emit('change', $event)"
          @keyup.enter="localValue = $refs.input.value"
          @keydown.down.prevent="onArrowDownOnTextBox"
          @keyup.esc.prevent="isPopupOpen = false"
          data-cy="single-value-fallback-txt"
          v-tooltip.html="tooltip" />
        <div class="input-group-append dropdown dropdown-default" :class="{ disabled: isReadonly }" v-if="attributeType && enableSuggestions && canWrite">
          <b-btn :class="!isLoading ? 'dropdown-toggle' : 'px-1 py-0'" type="button" @click="onButtonClick" tabindex="-1" data-cy="suggestion-expand-btn">
            <spinner :size="25" v-if="isLoading" />
          </b-btn>
        </div>
        <template v-if="$slots.append">
          <slot name="append"></slot>
        </template>
      </div>
    </div>
    <suggest-value-list
      ref="suggestlist"
      :field="this.field"
      :attributeType="this.attributeType"
      :attributeId="this.attributeId"
      :languageCode="this.language ? this.language.languageCode : null"
      :disable-non-default-suggestions="disableNonDefaultSuggestions"
      v-model:isPopupOpen="isPopupOpen"
      v-model:isLoading="isLoading"
      v-model="localValue"
      v-show="isPopupOpen && !isLoading"
      v-if="enableSuggestions && canWrite"
      :filter="filter"
      :has-default-values="this.defaultOnly">
    </suggest-value-list>
    <value-editor
      v-if="showEditorModal ?? false"
      v-model:visible="showEditorModal"
      :edit-value="this.localValue ?? ''"
      :data-type="this.dataType ?? ''"
      :field="this.field ?? ''"
      :language="this.language ?? ''"
      :value-or-fallback="this.valueOrFallback ?? ''"
      :tooltip-text="this.tooltipText ?? ''"
      :attributeType="this.attributeType ?? ''"
      :attribute-id="this.attributeId ?? ''"
      :attributes="this.source ?? {}"
      :can-edit="this.canEdit"
      :default-only="defaultOnly"
      @editedValue="UpdatedValue($event)">
    </value-editor>
  </div>
</template>

<script>
import ValueEditor from "@ContentEditor/views/AttributeValueEditor.vue";
import { createPopper } from "@popperjs/core";
import { NumberParser } from "@util/numberParse";
import _ from "lodash";
import { mapState } from "vuex";
import Spinner from "../../common/components/Spinner.vue";
import { fallbackPosition, sameWidth } from "../../plugins/popper-modifiers";
import authorizationProvider from "../../services/permissions/permissionEngine";
import { getLanguageDisplayName } from "../../util/languages";
import SuggestValueList from "../components/Articles/SuggestValueList.vue";
import DateValueEditor from "./DateValueEditor.vue";
import IntervalValueEditor from "./IntervalValueEditor.vue";

export default {
  components: { SuggestValueList, Spinner, ValueEditor },
  props: [
    "value",
    "source",
    "field",
    "language",
    "validationData",
    "attributeType",
    "attributeId",
    "dataType",
    "inputClass",
    "enableSuggestions",
    "disableNonDefaultSuggestions",
    "hasDefaultValues",
    "authorizationContext",
    "disableFallback",
    "readonly",
    "getOverflowContainerHeight",
    "tooltipText",
    "showEditor",
    "useUiLocale",
    "sourceToFieldPath"
  ],
  data() {
    return {
      internLocalValue: "",
      focused: false,
      settings: {
        IsFallbackHintVisible: true
      },
      isPopupOpen: false,
      filter: "",
      popperInstance: null,
      isLoading: false,
      showEditorModal: false
    };
  },
  methods: {
    onBlurHandler($event) {
      this.$emit("blur", $event);
      this.focused = false;
    },
    handleClickOutside() {
      this.isPopupOpen = false;
      this.popperInstance?.destroy();
      this.popperInstance = null;
    },
    onButtonClick() {
      this.filter = this.defaultOnly ? "" : this.localValue;
      if (!this.isPopupOpen) {
        this.isPopupOpen = true;
      } else {
        this.isPopupOpen = false;
        this.popperInstance?.destroy();
        this.popperInstance = null;
      }
    },
    setFilter(nv) {
      if (this.isPopupOpen && this.filter != nv) {
        this.filter = nv;
      }
    },
    onArrowDownOnTextBox() {
      this.isPopupOpen = true;
      if (this.$refs.suggestlist && this.$refs.suggestlist.length > 0) {
        this.$refs.suggestlist.focusItem(0);
      }
    },
    UpdatedValue(nv) {
      this.internLocalValue = nv;
      this.$emit("input", nv);
    }
  },
  computed: {
    ...mapState(["userSettings"]),
    localValue: {
      get() {
        return this.internLocalValue;
      },
      set(nv) {
        this.internLocalValue = nv;
        this.$emit("input", nv);
      }
    },
    tooltip: function () {
      let result = "";
      if (this.tooltipText) result += `<span>${this.tooltipText}</span>`;
      if (this.validationTooltipText) result += `<span>${this.validationTooltipText}</span>`;
      if (this.fallbackLanguageText) result += `<span>${this.fallbackLanguageText}</span>`;
      return result;
    },
    canWrite() {
      if (this.authorizationContext) {
        return authorizationProvider.canWrite(this.authorizationContext);
      }
      return true;
    },
    dataTypeEditor() {
      if (this.dataType && this.dataType == "AttributeDataTypeInterval") {
        return IntervalValueEditor;
      } else if (this.dataType && this.dataType == "AttributeDataTypeDate") {
        return DateValueEditor;
      } else {
        return null;
      }
    },
    isReadonly() {
      if (!this.canEdit) return true;

      return this.dataType == "AttributeDataTypeLogical" || this.defaultOnly;
    },
    canEdit() {
      return this.readonly !== true && this.canWrite && this.hasRequiredDataSet;
    },
    hasRequiredDataSet() {
      return !!this.source && !!this.language && !!this.field && !!this.source[this.languageCode];
    },
    languageCode() {
      return this.language ? this.language.languageCode : "";
    },
    fallbackLanguage() {
      if (this.language) return this.language.fallbackLanguageCode;
      return "";
    },
    valueOrFallback() {
      if (!this.source || !this.language) return { value: "", isFallback: false };

      if (this.localValue) return { value: this.localValue, isFallback: false };

      if (this.fallbackLanguage && !this.disableFallback) {
        let model = this.source[this.fallbackLanguage];
        if (model) {
          if (Array.isArray(this.sourceToFieldPath) && this.sourceToFieldPath.length > 0) {
            model = _.reduce(this.sourceToFieldPath, (currentObject, currentField) => currentObject[currentField], model);
          }
          const value = this.field ? model[this.field] : model;
          if (value) return { value, isFallback: true };
        }
      }
      return { value: "", isFallback: false };
    },
    fallbackTooltip() {
      return this.valueOrFallback.isFallback && this.valueOrFallback.value ? this.language.fallbackLanguageCode : "";
    },
    localValidationData() {
      if (this.validationData && !this.validationData.isValid && this.validationData.type == "error") {
        return this.validationData;
      } else if (this.dataTypeValidationData) {
        return this.dataTypeValidationData;
      } else {
        return this.validationData;
      }
    },
    dataTypeValidationData() {
      const parser = new NumberParser(this.useUiLocale ? this.userSettings.CurrentLocale : this.languageCode);
      let numberOfFractionDigits = 0;
      if (this.localValue && parser.parseLocaleStringToIndepentendNumberString(this.localValue).indexOf(".") !== -1) {
        numberOfFractionDigits = parser.parseLocaleStringToIndepentendNumberString(this.localValue).split(".")[1].length;
      }

      if (this.dataType && this.dataType == "AttributeDataTypeNumerical") {
        if (this.localValue && this.localValue != "") {
          if (
            this.localValue !=
            parser.parseNumberToLocaleString(parser.parseLocaleStringToNumber(this.localValue), { minimumFractionDigits: numberOfFractionDigits })
          ) {
            return {
              isValid: false,
              feedback: this.$t("validation.dataTypeNotNumerical"),
              type: "warning"
            };
          }
        }
      } else if (this.dataType && this.dataType == "Double") {
        if (
          !this.localValue ||
          this.localValue == "" ||
          this.localValue !=
            parser.parseNumberToLocaleString(parser.parseLocaleStringToNumber(this.localValue), { minimumFractionDigits: numberOfFractionDigits })
        ) {
          return {
            isValid: false,
            feedback: this.$t("validation.dataTypeNotDouble"),
            type: "warning"
          };
        }
      } else if (this.dataType && this.dataType == "Decimal") {
        if (
          !this.localValue ||
          this.localValue == "" ||
          this.localValue !=
            parser.parseNumberToLocaleString(parser.parseLocaleStringToNumber(this.localValue), { minimumFractionDigits: numberOfFractionDigits })
        ) {
          return {
            isValid: false,
            feedback: this.$t("validation.dataTypeNotDecimal"),
            type: "warning"
          };
        }
      } else if (this.dataType && this.dataType == "Integer") {
        if (
          !this.localValue ||
          this.localValue == "" ||
          this.localValue !=
            parser.parseNumberToLocaleString(parser.parseLocaleStringToNumber(this.localValue), { minimumFractionDigits: numberOfFractionDigits })
        ) {
          return {
            isValid: false,
            feedback: this.$t("validation.dataTypeNotInterval"),
            type: "warning"
          };
        }
      } else if (this.dataType && this.dataType == "Boolean") {
        if (!this.localValue || this.localValue == "" || (this.localValue.toLowerCase() != "true" && this.localValue.toLowerCase() != "false")) {
          return {
            isValid: false,
            feedback: this.$t("validation.dataTypeNotBoolean"),
            type: "warning"
          };
        }
      }
      return null;
    },
    validationTooltipText: function () {
      const validationFeedback =
        this.localValidationData && !this.localValidationData.isValid && this.localValidationData.feedback ? this.localValidationData.feedback : "";
      const isValidationFeedbackEmpty = validationFeedback == "" || validationFeedback === undefined ? "" : " ";
      return validationFeedback + isValidationFeedbackEmpty;
    },
    fallbackLanguageText: function () {
      let fallbackLanguageText = "";
      if (this.settings.IsFallbackHintVisible) {
        fallbackLanguageText =
          this.valueOrFallback.isFallback && this.valueOrFallback.value
            ? this.$t("FallbackLanguageHintForField", { field: this.$t(this.field) }) + " " + getLanguageDisplayName(this.language.fallbackLanguageCode) + "."
            : "";
      }
      return fallbackLanguageText;
    },
    defaultOnly: function () {
      if ((this.field === "valueUid" || this.field === "value" || this.field === "valueAbbreviation") && this.authorizationContext?.data?.defaultValueOnly) {
        return true;
      }

      if ((this.field === "unitUid" || this.field === "unit" || this.field === "unitAbbreviation") && this.authorizationContext?.data?.defaultUnitOnly) {
        return true;
      }

      return false;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(nv) {
        if (nv != this.localValue) {
          this.localValue = nv;
        }
      }
    },
    localValue(nv) {
      this.debounceFilterSet(nv);
    },
    userSettings: {
      immediate: true,
      handler(nv) {
        if (nv) {
          this.settings.IsFallbackHintVisible =
            nv.IsFallbackHintVisible === true || nv.IsFallbackHintVisible === "true" || nv.IsFallbackHintVisible === undefined;
        }
      }
    },
    isPopupOpen: function (nv) {
      if (!this.$refs.input) {
        return;
      }
      const inputs = ["INPUT", "TEXTAREA", "SELECT"]; //hopefully this wont grow bigger
      if (!nv && document.activeElement != this.$refs.input && (!document.activeElement || inputs.every((i) => i != document.activeElement.tagName)))
        this.$refs.input.focus();
      if (typeof this.$refs.input.selectionStart !== "undefined") this.$refs.input.selectionStart = this.$refs.input.value.length;
    },
    isLoading(nv) {
      if (nv == false && this.$refs.suggestlist) {
        // Maximum height is 300px or lower if the container height is smaller than 600px
        const popperHeight = this.getOverflowContainerHeight ? Math.min(Math.floor(((this.getOverflowContainerHeight() || 630) - 30) / 2), 300) : 300;
        // Set the maximum height before Popper.js init (important for flip function!)
        this.$refs.suggestlist.$el.style.maxHeight = `${popperHeight}px`;

        this.popperInstance = createPopper(this.$refs.inputWrapper, this.$refs.suggestlist.$el, {
          strategy: "fixed",
          placement: "bottom-start",
          modifiers: [sameWidth, fallbackPosition]
        });
      }
    },
    validationTooltipText: function () {
      if (this.$refs.input) {
        this.$refs.tooltip?.updateContent();
      }
    },
    tooltipText: function () {
      if (this.$refs.input) {
        this.$refs.tooltip?.updateContent();
      }
    },
    fallbackLanguageText: function () {
      if (this.$refs.input) {
        this.$refs.tooltip?.updateContent();
      }
    },
    showEditor: {
      immediate: true,
      handler(nv) {
        if (nv) {
          this.showEditorModal = nv;
        } else {
          this.showEditorModal = false;
        }
      }
    },

    showEditorModal(newVal) {
      this.$emit("update:showEditor", newVal);
    }
  },
  created() {
    this.debounceFilterSet = _.debounce((nv) => this.setFilter(nv), 500);
  }
};
</script>
